/**
 *
 * UserDetailPage
 *
 */
import * as React from 'react';
import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserDetailPage } from './slice/selectors';
import { useHistory, useParams } from 'react-router-dom';
import { useUserDetailPageSlice } from './slice';
import { useSubscriptionDetailPageSlice } from '../SubscriptionDetailPage/slice';
import { selectSubscriptionDetailPage } from '../SubscriptionDetailPage/slice/selectors';
import { SectionHeader } from 'app/components/Headers/SectionHeader';
import { PageHeader } from 'app/components/Headers';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { FormRegionPicker } from 'app/components/FormRegionPicker';
import { Grid, Fab, Checkbox, FormControlLabel } from '@mui/material';
import {
  FormTextControl,
  StickySaveForm,
  FormSearchableControl,
  SpinnerItem,
} from '@assetic/web-components';
import { FastField, Field, FieldProps, FormikHelpers } from 'formik';
import { FormCheckBoxCard } from 'app/components/FormCheckBoxCard';
import {
  User,
  UserMetaDataTypesPayload,
  ContentArrayPayload,
} from 'types/models';
import { selectPreloadState } from 'app/helpers/PreloadStateHook/slice/selectors';
import * as Yup from 'yup';
import { SubscriptionInfo } from './SubscriptionInfo';
import { DetailPageLoadingEl } from 'app/components/DetailPageLoadingEl';
import { UrlToUser } from 'app/constants';
import { CompanyInfo } from './CompanyInfo';

/* istanbul ignore next */
export const UserDetailSchema = Yup.object().shape({
  segment_name: Yup.string().required('Required').nullable(),
  first_name: Yup.string().required('Required').nullable(),
  last_name: Yup.string().required('Required').nullable(),
  locale: Yup.string().required('Required').nullable(),
});

export const UserMetaDataArea = (props: {
  metaError: boolean;
  metaData: ContentArrayPayload<UserMetaDataTypesPayload> | null;
}) => {
  let content;
  const { metaError, metaData } = props;
  if (metaError || (!metaError && !metaData?.contents?.length)) {
    content = (
      <div>
        Something wrong with the user metadata types, please try again later.
      </div>
    );
  } else {
    content = (
      <Grid container spacing={2}>
        {metaData?.contents.map(meta => {
          if (meta?.user_flag_id) {
            return (
              <Grid item lg={4} xs={12} key={meta.user_flag_id}>
                <Field
                  name="app_metadata.features"
                  type="checkbox"
                  value={meta.user_flag_id}
                  component={FormCheckBoxCard}
                  label={meta.user_flag_id}
                  helperText={meta.description}
                  size={'normal'}
                />
              </Grid>
            );
          }
          return null;
        })}
      </Grid>
    );
  }

  return (
    <>
      <SectionHeader title="Flags" />
      {content}
    </>
  );
};

export function UserDetailPage() {
  const {
    actions: { updateUserDetail, fetchUserPageCompanyInfo },
  } = useUserDetailPageSlice();
  const {
    actions: { fetchSubscription },
  } = useSubscriptionDetailPageSlice();

  const { user, userCompany, loading } = useSelector(selectUserDetailPage);
  const { subscription, loading: subLoading } = useSelector(
    selectSubscriptionDetailPage,
  );
  const {
    companySsoConnectionType: { loading: ssoLoading, data: ssoConnectionTypes },
    userMetaData: { loading: metaLoading, data: metaData, error: metaError },
    userRole: { loading: userRoleLoading, data: userRoleData },
  } = useSelector(selectPreloadState);

  const userRoleKeyValues = useMemo<SpinnerItem[]>(() => {
    if (userRoleData?.contents.length) {
      return userRoleData.contents.map(s => ({
        label: s,
        value: s,
      }));
    }
    return [];
  }, [userRoleData]);

  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const history = useHistory();

  React.useEffect(() => {
    if (subscription?.owner?.user_id !== id && user?.subscription_id) {
      dispatch(fetchSubscription(user.subscription_id));
    }
  }, [
    user?.subscription_id,
    id,
    dispatch,
    fetchSubscription,
    subscription?.owner?.user_id,
  ]);

  React.useEffect(() => {
    dispatch(fetchUserPageCompanyInfo(id));
  }, [id, dispatch, fetchUserPageCompanyInfo]);

  if (loading || metaLoading || subLoading || userRoleLoading || ssoLoading) {
    return <DetailPageLoadingEl />;
  }

  if (!user) {
    return (
      <div>
        There is something wrong on the user's information, please refresh and
        try again later.
      </div>
    );
  }

  const formOnSubmit = (value: User, action?: FormikHelpers<any>) => {
    const {
      locale,
      timezone,
      segment_name,
      first_name,
      last_name,
      job_title,
      department,
      organisation,
      app_metadata,
      portfolio_notification,
      app_user_role,
      user_metadata,
    } = value;

    const updateUserDetailObj: User = {
      locale,
      timezone,
      segment_name,
      first_name,
      last_name,
      job_title,
      department,
      organisation,
      app_metadata,
      portfolio_notification,
      app_user_role,
      user_metadata,
    };
    dispatch(
      updateUserDetail({
        id: user.id!,
        updatedUserDetail: updateUserDetailObj,
      }),
    );
  };

  const redirectToCreateSub = () => {
    history.push(UrlToUser(user.id).createSub, { ownerEmail: user.email });
  };

  return (
    <>
      <PageHeader text="User" icon={<AccountCircle />} />

      <StickySaveForm
        initialValues={user}
        validationSchema={UserDetailSchema}
        onSubmit={formOnSubmit}
        enabledUnsavedDetection
        enableReinitialize
        saveLabel={<>Save</>}
        render={_formProps => (
          <div>
            <SectionHeader title="Profile" />
            <Grid container spacing={2}>
              <Grid item lg={4} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FastField
                      name="auth_id"
                      label="Auth ID"
                      component={FormTextControl}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="id"
                      label="User ID"
                      component={FormTextControl}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="app_user_role"
                      label="User Role"
                      component={FormSearchableControl}
                      items={userRoleKeyValues}
                      itemsLoading={userRoleLoading}
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item lg={4} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FastField
                      name="email"
                      label="Email"
                      component={FormTextControl}
                      type="email"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="first_name"
                      id="first_name"
                      label="First Name"
                      component={FormTextControl}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="last_name"
                      label="Last Name"
                      component={FormTextControl}
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item lg={4} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FastField
                      name="organisation"
                      label="Organisation"
                      component={FormTextControl}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="department"
                      label="Department"
                      component={FormTextControl}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="job_title"
                      label="Job Title"
                      component={FormTextControl}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <SectionHeader title="Company" />
            <CompanyInfo
              {...userCompany}
              ssoConnectionTypes={ssoConnectionTypes}
            />
            <SectionHeader title="Subscription" />
            {!user.subscription_id ? (
              <Grid item xs={12}>
                <Fab
                  size="small"
                  variant="extended"
                  aria-label="Save"
                  color="secondary"
                  onClick={redirectToCreateSub}
                >
                  Create Subscription
                </Fab>
              </Grid>
            ) : (
              <SubscriptionInfo {...subscription} />
            )}
            <SectionHeader title="Authentication" />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FastField name="user_metadata.use_mfa">
                  {({ field }: FieldProps<boolean>) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          color="primary"
                          checked={field.value}
                        />
                      }
                      label={'Multi Factor Authentication'}
                    />
                  )}
                </FastField>
              </Grid>
            </Grid>
            <SectionHeader title="Communication" />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FastField name="portfolio_notification">
                  {({ field }: FieldProps<boolean>) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          color="primary"
                          checked={field.value}
                        />
                      }
                      label={
                        'Receive email notification when invited to a Portfolio'
                      }
                    />
                  )}
                </FastField>
              </Grid>
            </Grid>
            <FormRegionPicker />
            <UserMetaDataArea metaError={metaError} metaData={metaData} />
          </div>
        )}
      />
    </>
  );
}

export default UserDetailPage;
